import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { FEE_CATEGORIES, DEFAULT_ENABLED_CATEGORIES, FeeCategory } from '@/lib/data';

const STORAGE_KEY = 'avgiftsview-enabled-categories';

interface FeeCategoryContextValue {
  categories: FeeCategory[];
  enabledCategories: string[];
  isEnabled: (id: string) => boolean;
  toggleCategory: (id: string) => void;
}

const FeeCategoryContext = createContext<FeeCategoryContextValue | undefined>(undefined);

const readStoredCategories = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_ENABLED_CATEGORIES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_ENABLED_CATEGORIES;
    const validIds = new Set(FEE_CATEGORIES.map(c => c.id));
    const filtered = parsed.filter((id): id is string => typeof id === 'string' && validIds.has(id));
    return filtered.length > 0 ? filtered : DEFAULT_ENABLED_CATEGORIES;
  } catch {
    return DEFAULT_ENABLED_CATEGORIES;
  }
};

export const FeeCategoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabledCategories, setEnabledCategories] = useState<string[]>(readStoredCategories);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enabledCategories));
  }, [enabledCategories]);

  const isEnabled = (id: string) => enabledCategories.includes(id);

  const toggleCategory = (id: string) => {
    setEnabledCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const value = useMemo(
    () => ({ categories: FEE_CATEGORIES, enabledCategories, isEnabled, toggleCategory }),
    [enabledCategories]
  );

  return <FeeCategoryContext.Provider value={value}>{children}</FeeCategoryContext.Provider>;
};

export const useFeeCategories = (): FeeCategoryContextValue => {
  const context = useContext(FeeCategoryContext);
  if (!context) {
    throw new Error('useFeeCategories must be used within a FeeCategoryProvider');
  }
  return context;
};
